import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Clock, User, Tag } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Comments from "../components/Comments";
import { fetchArticle, fetchTechTopics, fetchArticles } from "../lib/api";
import { SectionLoader } from "../components/shared";
import { trackBlogCTAClick } from "../lib/analytics";

const gradients = {
  navy: "linear-gradient(135deg, #0A1F47 0%, #1C3D7A 100%)",
  blue: "linear-gradient(135deg, #0A1F47 0%, #2E6BF6 100%)",
  deep: "linear-gradient(135deg, #061433 0%, #0A1F47 100%)",
};

export default function ArticleDetail() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [techMap, setTechMap] = useState({});
  const [moreArticles, setMoreArticles] = useState([]);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    setStatus("loading");
    Promise.all([fetchArticle(slug), fetchTechTopics(), fetchArticles({ limit: 20 })])
      .then(([a, topics, all]) => {
        setArticle(a);
        setTechMap(Object.fromEntries(topics.map((t) => [t.slug, t])));
        setMoreArticles(all.filter((x) => x.slug !== a.slug).slice(0, 3));
        setStatus("ok");
      })
      .catch(() => setStatus("notfound"));
  }, [slug]);

  if (status === "loading") {
    return (
      <div className="min-h-screen nst-light">
        <Navbar variant="solid" />
        <div className="pt-[76px]"><SectionLoader /></div>
      </div>
    );
  }
  if (status === "notfound" || !article) {
    return (
      <div className="min-h-screen nst-light">
        <Navbar variant="solid" />
        <div className="pt-[140px] px-6 text-center">
          <h1 className="text-3xl font-bold text-[#0A1F47]">Article not found</h1>
          <Link to="/#insights" className="btn-navy mt-6">
            <ArrowLeft className="w-4 h-4" /> Back to Insights
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#EDF1FB]">
      <Navbar variant="solid" />

      {/* Hero */}
      <section
        className="relative overflow-hidden text-white pt-[140px] pb-20"
        style={{ background: gradients[article.gradient_key] || gradients.navy }}
      >
        <div className="absolute inset-0 bg-grid-dark opacity-30" />
        <div className="relative max-w-[900px] mx-auto px-6 lg:px-10">
          <Link to="/#insights" className="inline-flex items-center gap-2 text-sm font-semibold text-white/70 hover:text-white mb-8">
            <ArrowLeft className="w-4 h-4" /> All Insights
          </Link>

          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 text-white text-xs font-semibold backdrop-blur-sm border border-white/20">
            <Tag className="w-3 h-3" />
            {article.category}
          </span>

          <h1 className="mt-6 text-4xl md:text-5xl lg:text-6xl font-extrabold leading-[1.08]">
            {article.title}
          </h1>
          <p className="mt-5 text-lg text-white/80 leading-relaxed">{article.excerpt}</p>

          <div className="mt-8 flex items-center gap-6 text-sm text-white/70">
            <div className="flex items-center gap-2">
              <User className="w-4 h-4" /> {article.author}
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4" /> {article.read_time}
            </div>
            <div>{article.published_at}</div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="nst-light py-20">
        <div className="max-w-[760px] mx-auto px-6 lg:px-10">
          {article.tech_slugs?.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-10 pb-8 border-b border-[#D9E0F0]">
              <span className="text-xs uppercase tracking-wider text-[#0A1F47]/50 font-semibold mr-2 py-1.5">Tagged</span>
              {article.tech_slugs.map((ts) => {
                const t = techMap[ts];
                if (!t) return null;
                return (
                  <Link
                    key={ts}
                    to={`/tech/${ts}`}
                    className="px-3 py-1.5 text-xs font-semibold rounded-full border border-[#0A1F47]/15 text-[#0A1F47] hover:bg-[#0A1F47] hover:text-white transition-colors"
                  >
                    {t.name}
                  </Link>
                );
              })}
            </div>
          )}

          <article className="space-y-10">
            {article.content_sections?.map((sec, i) => (
              <section key={i}>
                {sec.heading && (
                  <h2 className="text-2xl md:text-3xl font-extrabold text-[#0A1F47] leading-tight mb-4">
                    {sec.heading}
                  </h2>
                )}
                {sec.body && (
                  <p className="text-[17px] leading-[1.75] text-[#0A1F47]/85">{sec.body}</p>
                )}
                {sec.bullets && (
                  <ul className="mt-5 space-y-2.5">
                    {sec.bullets.map((b) => (
                      <li key={b} className="flex items-start gap-3 text-[#0A1F47]/85">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#2E6BF6] mt-2.5 flex-shrink-0" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                )}
                {sec.callout && (
                  <div className="mt-5 p-5 rounded-xl bg-[#2E6BF6]/5 border-l-4 border-[#2E6BF6] text-[#0A1F47]/90 italic">
                    {sec.callout}
                  </div>
                )}
              </section>
            ))}
          </article>

          {/* CTA Section */}
          <div className="mt-16 p-8 md:p-10 rounded-2xl bg-gradient-to-br from-[#0A1F47] to-[#1a3a6b] text-white">
            <div className="max-w-2xl">
              <h3 className="text-2xl md:text-3xl font-bold mb-4">
                Ready to Transform Your Cloud Infrastructure?
              </h3>
              <p className="text-lg text-white/90 mb-6">
                Our principal-level engineers design and operate intelligent cloud platforms for enterprises. Let's discuss how we can accelerate your cloud journey.
              </p>
              <a
                href="/#contact"
                onClick={() => trackBlogCTAClick(article?.title || 'unknown')}
                className="inline-flex items-center gap-2 px-8 py-4 rounded-lg bg-[#2E6BF6] hover:bg-[#2558d6] text-white text-lg font-semibold transition-colors shadow-lg hover:shadow-xl"
              >
                Talk to a Cloud Architect
                <ArrowRight className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Internal Links Section */}
          <div className="mt-12 p-6 rounded-xl bg-[#F5F7FA] border border-[#E2E8F4]">
            <h3 className="text-xl font-bold text-[#0A1F47] mb-4 flex items-center gap-2">
              <span className="text-[#2E6BF6]">→</span> Related Resources
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Link
                to="/services/aiops-framework"
                className="group flex items-center gap-3 p-4 rounded-lg bg-white border border-[#E2E8F4] hover:border-[#2E6BF6] hover:shadow-md transition-all"
              >
                <div className="flex-1">
                  <h4 className="font-semibold text-[#0A1F47] group-hover:text-[#2E6BF6] transition-colors">
                    AIOps Framework Services
                  </h4>
                  <p className="text-sm text-[#0A1F47]/60 mt-1">
                    Intelligent monitoring and automation
                  </p>
                </div>
                <ArrowRight className="w-5 h-5 text-[#2E6BF6] flex-shrink-0 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/devops-tools"
                className="group flex items-center gap-3 p-4 rounded-lg bg-white border border-[#E2E8F4] hover:border-[#2E6BF6] hover:shadow-md transition-all"
              >
                <div className="flex-1">
                  <h4 className="font-semibold text-[#0A1F47] group-hover:text-[#2E6BF6] transition-colors">
                    DevOps Tools Lab
                  </h4>
                  <p className="text-sm text-[#0A1F47]/60 mt-1">
                    Explore our DevOps dashboards and tools
                  </p>
                </div>
                <ArrowRight className="w-5 h-5 text-[#2E6BF6] flex-shrink-0 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
            <div className="mt-4 pt-4 border-t border-[#E2E8F4]">
              <a
                href="/#contact"
                className="inline-flex items-center gap-2 text-[#2E6BF6] font-semibold hover:gap-3 transition-all"
              >
                Get a Platform Assessment
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* More articles */}
      {moreArticles.length > 0 && (
        <section className="nst-light py-20 border-t border-[#D9E0F0]">
          <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
            <h2 className="text-3xl md:text-4xl font-extrabold text-[#0A1F47]">
              More <span className="text-[#2E6BF6]">Insights</span>
            </h2>
            <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5">
              {moreArticles.map((a) => (
                <Link
                  key={a.slug}
                  to={`/insights/${a.slug}`}
                  className="group bg-white border border-[#E2E8F4] rounded-2xl p-6 card-lift hover:border-[#2E6BF6]/40"
                >
                  <span className="text-xs font-semibold text-[#2E6BF6] uppercase tracking-wider">{a.category}</span>
                  <h3 className="text-lg font-bold text-[#0A1F47] mt-2 group-hover:text-[#2E6BF6] transition-colors">{a.title}</h3>
                  <p className="mt-2 text-sm text-[#0A1F47]/70 line-clamp-2">{a.excerpt}</p>
                  <div className="mt-4 text-[#2E6BF6] text-sm font-semibold flex items-center gap-1 group-hover:gap-2 transition-all">
                    Read <ArrowRight className="w-4 h-4" />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Comments Section */}
      {status === "ok" && article && (
        <section className="py-16 bg-white">
          <div className="max-w-[900px] mx-auto px-6 lg:px-10">
            <Comments articleSlug={slug} />
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}
